'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { User, Provider } from '@supabase/supabase-js'
import { useRouter } from 'next/navigation'
import { Analytics } from '@/components/analytics/posthog-provider'
import { getRedirectUrl, isProduction } from '@/lib/env'

type AuthContextType = {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{
    error: Error | null
    data: any | null
  }>
  signInWithLinkedIn: () => Promise<{
    error: Error | null
    data: any | null
  }>
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{
    error: Error | null
    data: any | null
  }>
  signOut: () => Promise<void>
  checkOnboardingStatus: () => Promise<boolean>
  setOnboardingCompleted: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => ({ error: null, data: null }),
  signInWithLinkedIn: async () => ({ error: null, data: null }),
  signUp: async () => ({ error: null, data: null }),
  signOut: async () => {},
  checkOnboardingStatus: async () => false,
  setOnboardingCompleted: async () => {}
})

export const useAuth = () => {
  // Safety check for server-side rendering during static generation
  if (typeof window === 'undefined') {
    // During SSG, return default auth context instead of trying to use React context
    return {
      user: null,
      loading: true,
      signIn: async () => ({ error: null, data: null }),
      signInWithLinkedIn: async () => ({ error: null, data: null }),
      signUp: async () => ({ error: null, data: null }),
      signOut: async () => {},
      checkOnboardingStatus: async () => false,
      setOnboardingCompleted: async () => {}
    };
  }
  
  // Only use React context on the client side
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [mounted, setMounted] = useState(false)
  const router = useRouter()

  // Safety check to prevent unwanted effects during SSG
  useEffect(() => {
    setMounted(true);
  }, []);

  // Make sure we're using the right persistence mode
  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return;
    
    // Check if we need to set the persistence
    const setupPersistence = async () => {
      try {
        console.log('Setting up auth persistence...');
        // Set persistence to default to localStorage where available
        const { data: { session } } = await supabase.auth.getSession();
        
        // Update persistence if needed
        if (typeof window !== 'undefined') {
          // Force persistence to localStorage
          localStorage.setItem('supabase.auth.token', JSON.stringify({
            access_token: session?.access_token || '',
            refresh_token: session?.refresh_token || '',
          }));
          
          // Ensure cookies are also set for middleware
          if (session?.access_token) {
            // Set 30-day expiry for persistent session
            const thirtyDaysInSeconds = 30 * 24 * 60 * 60; // 30 days in seconds
            
            // Set both kebab-case and colon-separated versions for maximum compatibility
            document.cookie = `sb-access-token=${session.access_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            document.cookie = `sb-refresh-token=${session.refresh_token || ''}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            document.cookie = `sb:access:token=${session.access_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            document.cookie = `sb:refresh:token=${session.refresh_token || ''}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            
            // Also set project-specific cookie if we can determine the project ref
            const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/(?:http[s]?:\/\/)?([^.]*)\.supabase\.co/)?.[1];
            if (projectRef) {
              const authObject = {
                access_token: session.access_token,
                refresh_token: session.refresh_token || '',
                expires_at: new Date(Date.now() + thirtyDaysInSeconds * 1000).toISOString()
              };
              document.cookie = `sb-${projectRef}-auth-token=${encodeURIComponent(JSON.stringify(authObject))}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            }
            
            console.log('Auth persistence cookies updated with 30-day expiration');
          }
          
          // Also try to set session through the API
          if (session?.access_token) {
            const { data, error } = await supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token || '',
            });
            
            if (error) {
              console.error('Error setting persistence mode:', error);
            } else {
              console.log('Auth persistence mode updated');
            }
          }
        }
      } catch (e) {
        console.error('Error setting up auth persistence:', e);
      }
    };
    
    setupPersistence();
  }, [mounted]);

  // Identify user in PostHog when user changes
  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return;
    
    if (user) {
      // Log LinkedIn metadata for debugging
      if (user.app_metadata?.provider === 'linkedin_oidc') {
        console.log('LinkedIn user metadata:', user.user_metadata);
      }
      
      // Identify the user in PostHog
      Analytics.identify(user.id, {
        email: user.email,
        name: user.user_metadata?.full_name || '',
        role: user.user_metadata?.role || 'user',
        sign_up_date: user.created_at
      });

      // Set super properties that will be included with all events
      Analytics.register({
        user_id: user.id,
        authenticated: true,
        role: user.user_metadata?.role || 'user'
      });
    } else {
      // Reset user identification when logged out
      Analytics.reset();
    }
  }, [user, mounted]);

  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return;
    
    // Check active session
    const getSession = async () => {
      console.log('AuthProvider: Checking active session')
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('AuthProvider: Session fetch error:', error)
        } else {
          console.log('AuthProvider: Session found?', !!session)
          if (session?.user) {
            console.log('AuthProvider: User authenticated:', session.user.id)
          }
        }
        
        setUser(session?.user || null)
        setLoading(false)
      } catch (e) {
        console.error('AuthProvider: Unexpected error in getSession:', e)
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: string, session: any) => {
      console.log('AuthProvider: Auth state changed -', event)
      if (session?.user) {
        console.log('AuthProvider: User authenticated on state change:', session.user.id)
      } else {
        console.log('AuthProvider: No user after auth state change')
      }
      
      setUser(session?.user || null)
      setLoading(false)
      
      // Track auth events in PostHog
      if (event === 'SIGNED_IN') {
        Analytics.track('user_signed_in', {
          method: session?.user?.app_metadata?.provider || 'email',
          user_id: session?.user?.id
        });
      } else if (event === 'SIGNED_OUT') {
        Analytics.track('user_signed_out');
      }
      
      console.log('AuthProvider: Refreshing router...')
      router.refresh()
    })

    return () => {
      console.log('AuthProvider: Unsubscribing from auth events')
      subscription.unsubscribe()
    }
  }, [router, mounted]);

  const signIn = async (email: string, password: string) => {
    console.log('AuthProvider: Attempting sign in for:', email)
    try {
      // First clear any existing session
      await supabase.auth.signOut();
      
      // Then perform sign in
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        console.error('AuthProvider: Sign in error:', error)
        return { data, error }
      } 
      
      console.log('AuthProvider: Sign in successful', data.user?.id)
      
      // Store the session manually to ensure it persists
      if (data.session) {
        try {
          // Store in localStorage for backup
          localStorage.setItem('supabase.auth.token', JSON.stringify({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          }));
          
          console.log('AuthProvider: Session stored in localStorage');
          
          // Update user state before returning
          setUser(data.user)
          
          // Set cookies with proper attributes to ensure they're sent with requests
          // Use a 30-day expiry to ensure persistent sessions
          const thirtyDaysInSeconds = 30 * 24 * 60 * 60; // 30 days in seconds
          
          // Set both kebab-case and colon-separated versions for maximum compatibility
          document.cookie = `sb-access-token=${data.session.access_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
          document.cookie = `sb-refresh-token=${data.session.refresh_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
          document.cookie = `sb:access:token=${data.session.access_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
          document.cookie = `sb:refresh:token=${data.session.refresh_token}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
          
          // Also set project-specific cookie if we can determine the project ref
          const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/(?:http[s]?:\/\/)?([^.]*)\.supabase\.co/)?.[1];
          if (projectRef) {
            const authObject = {
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
              expires_at: new Date(Date.now() + thirtyDaysInSeconds * 1000).toISOString()
            };
            document.cookie = `sb-${projectRef}-auth-token=${encodeURIComponent(JSON.stringify(authObject))}; path=/; max-age=${thirtyDaysInSeconds}; SameSite=Lax`;
            console.log('AuthProvider: Set project-specific auth cookie');
          }
          
          console.log('AuthProvider: Auth cookies set with 30-day expiration');
          
          // Ensure session is properly saved before returning
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          
          console.log('AuthProvider: Session set in Supabase client');
        } catch (e) {
          console.error('AuthProvider: Error storing session:', e);
        }
      }
      
      return { data, error }
    } catch (e) {
      console.error('AuthProvider: Unexpected error during sign in:', e)
      return { data: null, error: e as Error }
    }
  }

  const signInWithLinkedIn = async () => {
    console.log('AuthProvider: Attempting to sign in with LinkedIn OIDC')
    try {
      // Determine the appropriate callback URL
      let callbackUrl;
      
      // In production, use the full production domain for the callback
      if (isProduction()) {
        callbackUrl = 'https://fintechtoronto.com/auth/callback';
        console.log('Using production callback URL:', callbackUrl);
      } else {
        // In development, use the dynamically determined URL
        callbackUrl = getRedirectUrl('/auth/callback');
        console.log('Using local callback URL for development:', callbackUrl);
      }
      
      // Clear any existing session before starting OAuth flow
      // This prevents conflicts between existing and new sessions
      await supabase.auth.signOut({ scope: 'local' });
      console.log('Cleared existing session before LinkedIn OAuth');
      
      // Add a timestamp to prevent cache issues
      const timestamp = new Date().getTime();
      
      // We need to construct a proper options object for the OAuth flow
      const options = {
        redirectTo: callbackUrl,
        queryParams: {
          flowType: 'pkce',
          ts: timestamp.toString() // Add timestamp to prevent caching issues
        },
        skipBrowserRedirect: false // Ensure browser redirection happens
      };
      
      // In production, also store the destination for retrieval after auth
      if (isProduction()) {
        // Store destination in localStorage for retrieval after auth
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth_redirect_path', '/dashboard/overview');
        }
      }
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options
      });
      
      if (error) {
        console.error('AuthProvider: LinkedIn sign in error:', error);
        return { data, error };
      }

      // Log the OAuth URL for debugging
      if (data?.url) {
        console.log('LinkedIn OAuth URL:', data.url);
      } else {
        console.warn('No redirect URL returned from LinkedIn OAuth setup');
      }

      // Handle OAuth response - note that we don't have session data yet
      // as the user will be redirected to LinkedIn
      if (data?.url) {
        // Store timestamp to help with debugging
        if (typeof window !== 'undefined') {
          localStorage.setItem('linkedin_auth_timestamp', timestamp.toString());
        }
        
        // Redirect will happen automatically, but we'll force it after a delay
        setTimeout(() => {
          console.log('Redirecting to LinkedIn for authentication...');
          window.location.href = data.url;
        }, 100);
      }
      
      return { data, error };
    } catch (e) {
      console.error('AuthProvider: Unexpected error during LinkedIn sign in:', e);
      return { data: null, error: e as Error };
    }
  }

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    console.log('AuthProvider: Attempting sign up for:', email)
    try {
      const fullName = `${firstName} ${lastName}`.trim()
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: fullName, // Keep full_name for backward compatibility
          },
        },
      })

      if (error) {
        console.error('AuthProvider: Sign up error:', error)
        return { data, error }
      }
      
      console.log('AuthProvider: Sign up successful', data.user?.id)

      // Create profile in the database if sign up is successful
      if (data.user) {
        console.log('AuthProvider: Creating user profile')
        try {
          const { error: profileError } = await supabase.from('profiles').upsert({
            id: data.user.id,
            email: email,
            first_name: firstName,
            last_name: lastName,
            full_name: fullName, // Keep full_name for backward compatibility
            created_at: new Date().toISOString(),
            onboarding_completed: false
          })
          
          if (profileError) {
            console.error('AuthProvider: Profile creation error:', profileError)
          }
        } catch (e) {
          console.error('AuthProvider: Error creating profile:', e)
        }
      }

      return { data, error }
    } catch (e) {
      console.error('AuthProvider: Unexpected error during sign up:', e)
      return { data: null, error: e as Error }
    }
  }

  const signOut = async () => {
    console.log('AuthProvider: Signing out user')
    try {
      // Clear all auth cookies before signout
      document.cookie = 'sb-access-token=; path=/; max-age=0; SameSite=Lax';
      document.cookie = 'sb-refresh-token=; path=/; max-age=0; SameSite=Lax';
      document.cookie = 'sb:access:token=; path=/; max-age=0; SameSite=Lax';
      document.cookie = 'sb:refresh:token=; path=/; max-age=0; SameSite=Lax';
      
      // Clear project-specific cookie if we can determine the project ref
      const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/(?:http[s]?:\/\/)?([^.]*)\.supabase\.co/)?.[1];
      if (projectRef) {
        document.cookie = `sb-${projectRef}-auth-token=; path=/; max-age=0; SameSite=Lax`;
      }
      
      // Clear localStorage
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('auth_redirect_path');
      
      // Now sign out through Supabase with scope 'global' to clear all devices
      await supabase.auth.signOut({ scope: 'global' });
      console.log('AuthProvider: User signed out')
      
      // Force page reload to clear any cached state
      window.location.href = '/auth/login';
    } catch (e) {
      console.error('AuthProvider: Error during sign out:', e)
    }
  }

  const checkOnboardingStatus = async () => {
    if (!user) return false

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('AuthProvider: Error checking onboarding status:', error)
        return false
      }

      return data?.onboarding_completed || false
    } catch (e) {
      console.error('AuthProvider: Unexpected error checking onboarding status:', e)
      return false
    }
  }

  const setOnboardingCompleted = async () => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id)

      if (error) {
        console.error('AuthProvider: Error setting onboarding status:', error)
      }
    } catch (e) {
      console.error('AuthProvider: Unexpected error setting onboarding status:', e)
    }
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      signIn,
      signInWithLinkedIn,
      signUp,
      signOut,
      checkOnboardingStatus,
      setOnboardingCompleted
    }}>
      {!mounted ? children : children}
    </AuthContext.Provider>
  )
}